import { IImage } from "@faire/web-api/indigofair/data/IImage";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Row } from "@faire/web/slate/Layout";
import { Spacer, SpacerMultipliersKey } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { Section } from "@faire/retailer/views/careers/__internal__/common/common";

export const BelongingLifeAtFaire: React.FC = () => {
  const { isMobile, isTabletAndBelow } = useViewport();
  const { strictLocalize } = useStrictLocalization();
  const bottomSpacing = useResponsiveValue<SpacerMultipliersKey | number>({
    mobileAndAbove: "10x",
    tabletAndAbove: "8x",
    desktopAndAbove: 120,
  });
  const isSSR = useIsSSR();
  return (
    <Section align="center">
      <HideOverflowRow justify="center">
        <img
          src={optimizeImageUrl(
            { url: OUTDOOR_IMAGE(strictLocalize).url },
            isSSR,
            {
              width: OUTDOOR_IMAGE(strictLocalize).width,
              height: OUTDOOR_IMAGE(strictLocalize).height,
            }
          )}
          width={OUTDOOR_IMAGE(strictLocalize).width}
          height={OUTDOOR_IMAGE(strictLocalize).height}
          alt={OUTDOOR_IMAGE(strictLocalize).alt_text}
        />
        {isMobile ? null : (
          <>
            <Spacer height="2x" />
            <img
              src={optimizeImageUrl(
                { url: FAIRENT_IMAGE(strictLocalize).url },
                isSSR,
                {
                  width: FAIRENT_IMAGE(strictLocalize).width,
                  height: FAIRENT_IMAGE(strictLocalize).height,
                }
              )}
              width={FAIRENT_IMAGE(strictLocalize).width}
              height={FAIRENT_IMAGE(strictLocalize).height}
              alt={FAIRENT_IMAGE(strictLocalize).alt_text}
            />
          </>
        )}
        {isTabletAndBelow ? null : (
          <>
            <Spacer height="2x" />
            <img
              src={optimizeImageUrl(
                { url: GROUP_OFFICE_IMAGE(strictLocalize).url },
                isSSR,
                {
                  width: GROUP_OFFICE_IMAGE(strictLocalize).width,
                  height: GROUP_OFFICE_IMAGE(strictLocalize).height,
                }
              )}
              width={GROUP_OFFICE_IMAGE(strictLocalize).width}
              height={GROUP_OFFICE_IMAGE(strictLocalize).height}
              alt={GROUP_OFFICE_IMAGE(strictLocalize).alt_text}
            />
          </>
        )}
      </HideOverflowRow>
      <Spacer height="5x" />
      <Typography variant="sectionHeaderSansMedium" align="center">
        <StrictLocalMsg
          defaultMessage="Follow us on <LinkFormatting>@lifeatfaire</LinkFormatting>"
          description={{
            text: "text to encourage viewer to follow us on instagram",
          }}
          values={{
            LinkFormatting: (chunks) => (
              <NoUnderlineLink linkTo="https://instagram.com/lifeatfaire">
                {chunks}
              </NoUnderlineLink>
            ),
          }}
        />
      </Typography>
      <Spacer height={bottomSpacing} />
    </Section>
  );
};

const FAIRENT_IMAGE = (strictLocalize: StrictLocalizeFunction): IImage =>
  IImage.build({
    url: "https://cdn.faire.com/fastly/7b7543193fc2393fcf46b22fabc4200fc40d428d67ea5f6fa5fe277f1c3edede.png",
    width: 340,
    height: 340,
    alt_text: strictLocalize({
      defaultMessage: "Faire parent with her baby",
      description: {
        text: "Alt text for an image of a Faire parent with her baby",
      },
    }),
  });

const OUTDOOR_IMAGE = (strictLocalize: StrictLocalizeFunction): IImage =>
  IImage.build({
    url: "https://cdn.faire.com/fastly/3251770535d2f2f35c628a923646b1cf04bfa5c429f1f03f03d6e86b6c5bf38f.png",
    width: 340,
    height: 340,
    alt_text: strictLocalize({
      defaultMessage: "Faire employee group photo outdoors",
      description: {
        text: "Alt text for an image of Faire employee group photo outdoors",
      },
    }),
  });

const GROUP_OFFICE_IMAGE = (strictLocalize: StrictLocalizeFunction): IImage =>
  IImage.build({
    url: "https://cdn.faire.com/fastly/c36a834475ff1e8c89271598bc67c805e44ce9a703bac09d0f9c98686c478149.png",
    width: 340,
    height: 340,
    alt_text: strictLocalize({
      defaultMessage: "Employee group photo in a Faire office",
      description: {
        text: "Alt text for an image of an employee group photo in a Faire office",
      },
    }),
  });

const NoUnderlineLink = styled(EmbeddedLink)`
  text-decoration: auto;
`;

const HideOverflowRow = styled(Row)`
  overflow: hidden;
`;
